import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  memo,
  useMemo,
} from "react";
import { useI18n } from "Hooks";
import _ from "lodash";
import classNames from "classnames";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import withDerivedProps from "lib/withDerivedProps";

// dnd kit imports
import {
  DndContext,
  useSensors,
  useSensor,
  MouseSensor,
  TouchSensor,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement } from "@dnd-kit/modifiers";

// modules
import {
  bulkCreateStandardRubricCriteria,
  removeStandardRubricCriterias,
  removeStandardRubricDescriptors,
  createStandardRubricDescriptor,
  updateStandardRubricDescriptors,
  updateScoreToolData,
  updateStandardRubricCriterias,
} from "../../modules/StandardRubricModule";
import {
  getBenchmarkGradeScaleDescriptorsQuery,
  getGradeScaleValuesQuery,
  getStandardGradeScaleDescriptorsQuery,
} from "../../modules/StandardRubricQuery";
import { updateSettings } from "modules/ConfigurationThunks";
import { saveAssessmentToolUpdateStatusInRedux } from "Assessments/modules/AssessmentModule";
import {
  reorderAssessmentToolItem,
  updateAssessmentTool,
  updateAssessmentForStandardBasedRubric,
} from "modules/AssessmentTool/AssessmentToolModule";
import {
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";

// components
import {
  SortableRow,
  SortableDescriptor,
} from "./components/DragAndDropHelpers/DragAndDropHelpers";
import StandardRubricEditCell from "./components/StandardRubricEditCell";
import FormTextInput from "UIComponents/InputTextField/FormTextInput";
import StandardsSelectionModal from "AppComponents/StandardsSelectionModal";
import SkeletonLoader from "UIComponents/SkeletonLoader";

// design system
import {
  DSProvider,
  Button,
  IconButton,
  ChecklistDropdown,
  Alert,
  ToggleSwitch,
} from "@toddle-edu/ds-web";
import {
  AddCircleOutlined,
  AssessmentAddOutlined,
  SortOutlined,
  UpDownArrowOutlined,
  RightArrowOutlined,
  LeftArrowOutlined,
  ReloadArrowOutlined,
} from "@toddle-edu/ds-icons";

// utils and constants
import {
  getDisplayOptions,
  getElementType,
  getIsGradeScaleValueSelectionAllowed,
  getTransformedAssessmentTool,
} from "../../modules/Utils";
import { findDuplicates } from "Utils/NumberHelpers";
import { moveElementInArray, scrollIntoView, delay } from "Utils";
import {
  DEFAULT_CRITERION_INPUT,
  ROW_CELL_CONTAINER,
  SCORE_BASED_RUBRIC_ROW,
  STANDARD_RUBRIC_DISPLAY_SETTINGS,
  SPLIT_VIEW_DESCRIPTION,
} from "../../modules/Constants";
import { getSettingValue } from "Utils/ConfigurationUtils";
import {
  SCORE_BASED_RUBRIC,
  STANDARD_BASED_RUBRIC,
  CURRICULUM_TYPE_PYP,
} from "Constants/stringConstants";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";

// styles
import classes from "./StandardRubricEdit.scss";

const INITIAL_INPUT_WIDTH = "40px";

const styles = {
  arrowOutlined: {
    transform: "rotate(90deg)",
  },
  inputStyle: {
    minWidth: INITIAL_INPUT_WIDTH,
  },
  inputContainerStyle: {
    width: "min-content",
  },
};

const activationConstraint = {
  distance: 10,
  delay: 0,
  tolerance: 0,
};

const coordinateGetter = (event, args) => {
  return sortableKeyboardCoordinates(event, args);
};

const StandardRubricEdit = memo(props => {
  const {
    showSaveTemplateButton = true,
    customRef,
    assessmentTool,
    onClickToolSave,
    testId,
    type = SCORE_BASED_RUBRIC,
    learningCourseId,
    gradeScaleValues,
    allowGradeScaleValueSelection,
    standardGradeScaleValueDescriptorsMapping,
    assessmentId,
    standardField,
    isTemplate = false, // whether the tool is being opened from "Select a template"

    // action creators
    updateAssessmentForStandardBasedRubric,
    updateSettings,
    updateAssessmentTool,
    updateScoreToolData,
    bulkCreateStandardRubricCriteria,
    updateStandardRubricCriterias,
    removeStandardRubricCriterias,
    removeStandardRubricDescriptors,
    createStandardRubricDescriptor,
    updateStandardRubricDescriptors,
    reorderAssessmentToolItem,
  } = props;

  const { t } = useI18n();

  const {
    standardRubricCriterias: criteria,
    gradeScale,
    associateScoreTool,
    metadata,
  } = assessmentTool || {};

  const displayOptions = useMemo(() => getDisplayOptions({ t, type }), [
    t,
    type,
  ]);

  // states
  const [maxScore, setMaxScore] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [descriptionConfig, setDescriptionConfig] = useState(() => {
    if (metadata?.selectedDisplayOptions) {
      const result = _.map(metadata.selectedDisplayOptions, option => {
        return _.find(displayOptions, { value: option }) || {};
      });

      return result;
    }

    return displayOptions;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [localRubricCriteria, setLocalRubricCriteria] = useState(criteria);
  const [sortDirection, setSortDirection] = useState(null); // null = unsorted, 'asc' = ascending, 'desc' = descending
  const [inputWidth, setInputWidth] = useState(INITIAL_INPUT_WIDTH);
  const [standardPutAfterItemId, setStandardPutAfterItemId] = useState(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isSortIconDisabled, setIsSortIconDisabled] = useState(false);
  const [isScoreToolEnabled, setIsScoreToolEnabled] = useState(
    () => !_.isEmpty(associateScoreTool)
  );
  const [isScoreToolLoading, setIsScoreToolLoading] = useState(false);

  const isStandardBasedRubric = type === STANDARD_BASED_RUBRIC;
  const showScore = isScoreToolEnabled;

  const settingName = STANDARD_RUBRIC_DISPLAY_SETTINGS[type];

  const isNumericGradeScale = gradeScale
    ? gradeScale?.gradingType === "NUMERIC"
    : null;

  const showScoreForNumericStandardBasedRubric =
    isStandardBasedRubric && isNumericGradeScale;

  const showSortingOption = showScore || isStandardBasedRubric;

  // refs
  const labelTextAreaRefs = useRef({});
  const descriptionTextAreaRefs = useRef({});
  const pointTextAreaRefs = useRef({});
  const sortButtonRef = useRef(null);
  const isSortOrderSet = useRef(false);
  const rubricEditCellRefs = useRef({});

  // function to flush all debounced functions in all StandardRubricEditCell component
  const flushAllDebouncedFunctions = useCallback(() => {
    _.values(rubricEditCellRefs.current).forEach(ref => {
      ref?.flushDebouncedFunctions?.();
    });
  }, []);

  // initialize dnd sensors
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint }),
    useSensor(TouchSensor, { activationConstraint }),
    useSensor(PointerSensor, { activationConstraint }),
    useSensor(KeyboardSensor, { coordinateGetter })
  );

  useEffect(() => {
    setLocalRubricCriteria(criteria);
  }, [criteria]);

  // Sync local isScoreToolEnabled state with associateScoreTool from props
  useEffect(() => {
    setIsScoreToolEnabled(!_.isEmpty(associateScoreTool));
  }, [associateScoreTool]);

  // Determine initial sort direction based on all criteria - runs when component mounts or when criteria changes
  useEffect(() => {
    if (
      _.size(localRubricCriteria) <= 0 ||
      !showSortingOption ||
      (isSortOrderSet.current && !isDragging)
    ) {
      return;
    }

    if (isStandardBasedRubric) {
      let isGradeValueAscending = true;
      let isGradeValueDescending = true;
      let hasValidGradeValues = false;

      for (const criterion of localRubricCriteria) {
        const descriptors = criterion?.descriptors || [];

        if (_.size(descriptors) > 1) {
          hasValidGradeValues = true;

          // Extract grade values as numbers, using gradeScaleValues as reference
          const gradeValues = _.map(descriptors, descriptor => {
            const gradeScaleValueId = _.get(descriptor, "gradeScaleValue.id");
            const matchingGradeScaleValue = _.find(gradeScaleValues, {
              key: gradeScaleValueId,
            });
            return _.toNumber(
              _.get(
                matchingGradeScaleValue,
                "gradeScaleValueScores[0].gradeValue"
              )
            );
          });

          // Check if the grade values are in ascending order
          for (let i = 1; i < gradeValues.length; i++) {
            if (gradeValues[i] < gradeValues[i - 1]) {
              isGradeValueAscending = false;
            }
            if (gradeValues[i] > gradeValues[i - 1]) {
              isGradeValueDescending = false;
            }
          }
        }
      }

      // Set sort direction based on grade value evaluation
      if (hasValidGradeValues) {
        if (isGradeValueAscending) {
          setSortDirection("asc");
        } else if (isGradeValueDescending) {
          setSortDirection("desc");
        } else {
          setSortDirection(null);
        }

        isSortOrderSet.current = true;
        return;
      }
    }

    // First, check if all scores across all criteria are empty
    const allScoresEmpty = _.every(localRubricCriteria, criterion => {
      return _.every(
        criterion?.descriptors || [],
        descriptor => _.isNil(descriptor.score) || descriptor.score === ""
      );
    });

    if (allScoresEmpty) {
      // If all scores are empty, determine sort order based on labels
      let isLabelAscending = true;
      let isLabelDescending = true;
      let hasValidLabels = false;

      for (const criterion of localRubricCriteria) {
        const descriptors = criterion?.descriptors || [];

        if (descriptors.length > 1) {
          hasValidLabels = true;

          // Get labels to compare
          const labels = _.map(
            descriptors,
            descriptor =>
              descriptor?.gradeScaleValue?.abbreviation || descriptor?.label
          );

          // Check if labels are sorted
          for (let i = 1; i < labels.length; i++) {
            if (labels[i].localeCompare(labels[i - 1]) < 0) {
              isLabelAscending = false;
            }
            if (labels[i].localeCompare(labels[i - 1]) > 0) {
              isLabelDescending = false;
            }
          }
        }
      }

      if (hasValidLabels) {
        if (isLabelAscending) {
          setSortDirection("asc");
        } else if (isLabelDescending) {
          setSortDirection("desc");
        } else {
          setSortDirection(null);
        }

        isSortOrderSet.current = true;
      }

      return;
    }

    // If not all scores are empty, use the original logic for numeric sorting
    let isAscending = true;
    let isDescending = true;
    let hasValidCriteria = false;

    for (const criterion of localRubricCriteria) {
      const descriptors = criterion?.descriptors || [];

      // Only consider criteria that have at least 2 descriptors with valid scores
      const validScoreDescriptors = _.filter(
        descriptors,
        descriptor =>
          !_.isNil(descriptor.score) &&
          !_.isUndefined(descriptor.score) &&
          descriptor.score !== ""
      );

      if (validScoreDescriptors.length > 1) {
        hasValidCriteria = true;

        // Extract scores as numbers
        const scores = _.map(validScoreDescriptors, descriptor =>
          _.toNumber(descriptor.score)
        );

        // Check if the scores are in ascending order
        for (let i = 1; i < scores.length; i++) {
          if (scores[i] < scores[i - 1]) {
            isAscending = false;
          }
          if (scores[i] > scores[i - 1]) {
            isDescending = false;
          }
        }
      }
    }

    // Set sort direction based on all criteria evaluation
    if (hasValidCriteria) {
      if (isAscending) {
        setSortDirection("asc");
      } else if (isDescending) {
        setSortDirection("desc");
      } else {
        setSortDirection(null);
      }

      isSortOrderSet.current = true;
    }
  }, [
    localRubricCriteria,
    isDragging,
    showSortingOption,
    isNumericGradeScale,
    isStandardBasedRubric,
    gradeScaleValues,
  ]);

  // scroll to the rubric and highlight invalid descriptors (empty scores) and empty labels
  useEffect(() => {
    if (!customRef) return;

    const isValid = () => {
      const invalidFields = { labels: [], scores: [] };
      let duplicateScoresCount = 0;
      let duplicateGradeValuesCount = 0;

      // Collect all invalid fields
      _.forEach(localRubricCriteria, criterion => {
        const { descriptors, id, label, standard } = criterion || {};

        // Check criterion label
        if (_.isEmpty(standard?.label?.trim() || label?.trim())) {
          invalidFields.labels.push(id);
        }

        // Check for duplicate scores
        if (showScore && !isStandardBasedRubric) {
          const duplicateScores = findDuplicates(_.map(descriptors, "score"));
          duplicateScoresCount += _.size(duplicateScores);
        }

        // Check for duplicate grade scale values
        if (allowGradeScaleValueSelection && !isNumericGradeScale) {
          const duplicateGradeValues = findDuplicates(
            _.map(descriptors, "gradeScaleValue.id")
          );
          duplicateGradeValuesCount += _.size(duplicateGradeValues);
        }

        // Check descriptor scores and labels
        _.forEach(descriptors, descriptor => {
          const { id, label, score, gradeScaleValue } = descriptor || {};
          if (showScore && _.isNil(score)) {
            invalidFields.scores.push(id);
          }

          if (_.isEmpty(_.trim(label || gradeScaleValue?.abbreviation))) {
            invalidFields.labels.push(id);
          }
        });
      });

      const totalInvalidCount =
        _.size(invalidFields.labels) +
        _.size(invalidFields.scores) +
        duplicateScoresCount +
        duplicateGradeValuesCount;

      // Handle invalid fields if any exist
      if (totalInvalidCount > 0) {
        // Scroll to rubric
        scrollIntoView({ element: document.getElementById(type) });

        // Helper function to highlight invalid fields
        const highlightInvalidFields = (ids, refs, getPlaceholderText) => {
          _.forEach(ids, (id, index) => {
            const element = refs.current[id];

            if (element) {
              if (index === 0) element.focus();

              // for allowGradeScaleValueSelection scale we show dropdown with button
              if (element.tagName === "BUTTON") {
                element.classList.add(classes.borderCritical);
              } else {
                const requiredText = t("common:lowercase", {
                  text: t("common:required"),
                });
                element.placeholder = `${getPlaceholderText(
                  id
                )} (${requiredText})`;
                element.classList.add(classes.errorPlaceholder);
              }
            }
          });
        };

        if (_.size(invalidFields.scores) > 0) {
          highlightInvalidFields(invalidFields.scores, pointTextAreaRefs, () =>
            t("common:point")
          );
        }

        if (_.size(invalidFields.labels) > 0) {
          highlightInvalidFields(
            invalidFields.labels,
            labelTextAreaRefs,
            id => {
              const isCriteria = _.some(localRubricCriteria, { id });
              return isCriteria ? t("common:criteria") : t("common:definition");
            }
          );
        }
      }

      return totalInvalidCount;
    };

    customRef({ isValid });
  }, [
    t,
    type,
    customRef,
    localRubricCriteria,
    maxScore,
    showScore,
    isNumericGradeScale,
    isStandardBasedRubric,
    allowGradeScaleValueSelection,
  ]);

  // variables
  const toolId = assessmentTool?.id;

  // determine if criteria delete button should be shown
  const showCriteriaDeleteButton = _.size(localRubricCriteria) > 1;

  // calculate total rubric score from first rubric's criteria
  const rubricTotalScore = useMemo(() => {
    return _.sumBy(localRubricCriteria, criterion => {
      const score = _.toNumber(criterion?.maxScore);
      return _.isNaN(score) ? 0 : score;
    });
  }, [localRubricCriteria]);

  const scoreToolMaxScore = _.toNumber(
    _.get(assessmentTool, "associateScoreTool.scoreToolData.maxScore")
  );

  const isMaxScoreOverridden =
    scoreToolMaxScore &&
    _.toString(rubricTotalScore) !== _.toString(scoreToolMaxScore);

  const calculateInputWidth = useCallback(value => {
    // Default minimum width of 60px for empty or very short values
    if (!value || value.length <= 2) {
      setInputWidth(INITIAL_INPUT_WIDTH);
      return;
    }

    // Calculate width based on content length
    // Each character is approximately 8-10px wide, adding some padding
    const calculatedWidth = `${Math.min(
      Math.max(value.length * 10, 60),
      120
    )}px`;

    setInputWidth(calculatedWidth);
  }, []);

  useEffect(() => {
    if (maxScore !== null && maxScore !== undefined) {
      calculateInputWidth(String(maxScore));
    }
  }, [maxScore, calculateInputWidth]);

  useEffect(() => {
    setMaxScore(scoreToolMaxScore);
  }, [scoreToolMaxScore]);

  const criteriaItems = _.compact(_.map(localRubricCriteria, c => c.id));

  const disableAddCriterion = _.some(criteriaItems, id =>
    _.startsWith(id, "NEW_")
  );

  const hasValidDescriptors = useMemo(() => {
    return !_.some(localRubricCriteria, criterion =>
      _.some(criterion.descriptors, descriptor =>
        _.startsWith(descriptor.id, "NEW_")
      )
    );
  }, [localRubricCriteria]);

  const isValid = !disableAddCriterion && hasValidDescriptors;

  // functions

  const onCloseAlert = () => {
    setShowAlert(false);
  };

  const onDragStart = useCallback(
    event => {
      setIsDragging(true);

      const activeId = _.get(event, "active.id", null);

      // Add dragging class to the element being dragged (highlight it)
      const isRow = _.some(localRubricCriteria, { id: activeId });

      const elementId = isRow
        ? `${SCORE_BASED_RUBRIC_ROW}_${activeId}`
        : `${ROW_CELL_CONTAINER}_${activeId}`;

      const element = document.getElementById(elementId);

      element?.classList?.add(classes.dragging);

      // set if row is being dragged or column
      if (isRow) {
        setActiveItem("STANDARD_RUBRIC_CRITERIA");
      } else {
        setActiveItem("STANDARD_RUBRIC_DESCRIPTOR");
      }
    },
    [localRubricCriteria]
  );

  const onDragMove = useCallback(
    event => {
      const { active, over } = event;
      const { id: overId } = over || {};
      const { id: activeId } = active || {};

      if (!active || !over) return;

      const overIndex = over?.data?.current?.sortable?.index;

      // first row has borderTopLeftRadius and borderTopRightRadius as 0px (handled through css)
      // so when a item is dragged over to first index, it should have borderTopLeftRadius and borderTopRightRadius as 0px
      // and the rest of the rows should have borderTopLeftRadius and borderTopRightRadius as 8px
      if (activeItem === "STANDARD_RUBRIC_CRITERIA") {
        const activeElementId = `${SCORE_BASED_RUBRIC_ROW}_${activeId}`;
        const overElementId = `${SCORE_BASED_RUBRIC_ROW}_${overId}`;

        const activeElement = document.getElementById(activeElementId);
        const overElement = document.getElementById(overElementId);

        if (!activeElement || !overElement) return;

        let borderRadius = "";

        if (overIndex === 0) {
          activeElement.style.borderTopLeftRadius = "0";
          activeElement.style.borderTopRightRadius = "0";

          borderRadius = "8px";
        } else {
          activeElement.style.borderTopLeftRadius = "8px";
          activeElement.style.borderTopRightRadius = "8px";

          borderRadius = "0";
        }

        _.forEach(localRubricCriteria, ({ id }) => {
          if (id !== activeId) {
            const elementId = `${SCORE_BASED_RUBRIC_ROW}_${id}`;
            const element = document.getElementById(elementId);

            if (element) {
              element.style.borderTopLeftRadius = borderRadius;
              element.style.borderTopRightRadius = borderRadius;
            }
          }
        });
        // last descriptor has rounded corners
        // first row descriptor has borderBottomRightRadius as 8px and borderTopRightRadius as 0px
        // remaining rows descriptor has borderBottomRightRadius as 8px and borderTopRightRadius as 8px
        // maintain this as we drag the descriptor
      } else if (activeItem === "STANDARD_RUBRIC_DESCRIPTOR") {
        const activeElementId = `ROW_CELL_CONTAINER_${activeId}`;
        const activeElement = document.getElementById(activeElementId);

        if (!activeElement) return;

        const criteriaWithOverDescriptor = _.find(
          localRubricCriteria,
          criterion => _.some(criterion.descriptors, { id: overId })
        );

        const isFirstCriterion =
          _.findIndex(localRubricCriteria, {
            id: criteriaWithOverDescriptor?.id,
          }) === 0;

        const descriptors = criteriaWithOverDescriptor?.descriptors || [];

        const isLastCell = _.last(descriptors)?.id === overId;

        if (isLastCell) {
          if (!isFirstCriterion) {
            activeElement.style.borderTopRightRadius = "8px";
          }

          activeElement.style.borderBottomRightRadius = "8px";
          activeElement.style.overflow = "auto";
        } else {
          if (!isFirstCriterion) {
            activeElement.style.borderTopRightRadius = "0";
          }

          activeElement.style.borderBottomRightRadius = "0";
          activeElement.style.overflow = "";
        }
      }
    },
    [activeItem, localRubricCriteria]
  );

  const onDragEnd = useCallback(
    async event => {
      const { active, over } = event;

      if (!active || !over) return;

      const activeId = _.get(active, "id", null);
      const overId = _.get(over, "id", null);

      // remove dragging class which we applied on drag start
      const isRow = _.some(localRubricCriteria, { id: activeId });

      const activeElementId = isRow
        ? `${SCORE_BASED_RUBRIC_ROW}_${activeId}`
        : `ROW_CELL_CONTAINER_${activeId}`;

      const activeElement = document.getElementById(activeElementId);

      if (activeElement) {
        activeElement?.classList?.remove(classes.dragging);
      }

      let items = null;

      if (activeItem === "STANDARD_RUBRIC_DESCRIPTOR") {
        const criteriaWithDescriptor = _.find(localRubricCriteria, criterion =>
          _.some(criterion.descriptors, { id: activeId })
        );

        items = criteriaWithDescriptor?.descriptors || [];
      } else {
        items = localRubricCriteria;
      }

      const fromIndex = _.findIndex(items, { id: activeId });
      const toIndex = _.findIndex(items, { id: overId });

      if (activeId !== overId) {
        let putAfterItemId = null;
        if (toIndex > 0) {
          if (fromIndex < toIndex) {
            putAfterItemId = _.get(items, `${[toIndex]}.id`, null);
          } else {
            putAfterItemId = _.get(items, `${[toIndex - 1]}.id`, null);
          }
        } else if (toIndex == _.size(items) - 1) {
          putAfterItemId = _.get(items.last(), `id`, null);
        }

        const updatedValue = moveElementInArray({
          array: items,
          fromIndex,
          toIndex,
        });

        // update local array, so that the UI updates instantly
        // NOTE: we're also updating cache, but there is a UI jump (need to debug it in the future then we can remove local state)
        if (activeItem === "STANDARD_RUBRIC_CRITERIA") {
          setLocalRubricCriteria(updatedValue);
        } else if (activeItem === "STANDARD_RUBRIC_DESCRIPTOR") {
          setLocalRubricCriteria(prevCriteria => {
            return _.map(prevCriteria, criterion => {
              if (_.some(criterion.descriptors, { id: activeId })) {
                return {
                  ...criterion,
                  descriptors: updatedValue,
                };
              }
              return criterion;
            });
          });
        }

        await reorderAssessmentToolItem({
          itemId: activeId,
          itemType: activeItem,
          putAfterItemId,
          toolType: type,
          toolId: assessmentTool?.id,
          updatedValue,
        });

        setIsDragging(false);
      } else {
        setIsDragging(false);
      }
    },
    [activeItem, assessmentTool, localRubricCriteria]
  );

  const saveStandardRubricUpdateActivityStatus = () => {
    const { assignmentId, saveAssessmentToolUpdateStatusInRedux } = props;
    if (assignmentId) {
      saveAssessmentToolUpdateStatusInRedux({
        assignmentId,
        toolType: type,
      });
    }
  };

  // display options change handler
  const onChangeDisplayOption = params => {
    // 1. update local state of user selection
    const selectedOptions = _.filter(displayOptions, option =>
      _.includes(params, option?.value)
    );

    setDescriptionConfig(selectedOptions);

    // 2. set user preference

    const selectedDisplayOptions = _.map(selectedOptions, "value");

    const userPreferredDisplayOptions = getSettingValue({
      name: settingName,
      readFromUserAndOrgLevel: true,
    });
    if (_.includes(userPreferredDisplayOptions, SPLIT_VIEW_DESCRIPTION)) {
      selectedDisplayOptions.push(SPLIT_VIEW_DESCRIPTION);
    }

    updateSettings({
      settings: [
        {
          name: settingName,
          value: JSON.stringify(selectedDisplayOptions),
        },
      ],
      showToastMsg: false,
    });

    // 3. update assessment tool metadata (it'll be used in evaluation, view mode, pdf) etc
    updateAssessmentTool({
      input: {
        id: toolId,
        metadata: { ...(metadata || {}), selectedDisplayOptions },
      },
    });
  };

  const onToggleMaxScore = useCallback(
    async currentIsScoreToolEnabled => {
      const updatedIsScoreToolEnabled = !currentIsScoreToolEnabled;

      setIsScoreToolEnabled(updatedIsScoreToolEnabled);
      setIsScoreToolLoading(true);

      try {
        await updateAssessmentTool({
          input: {
            id: toolId,
            isScoreToolEnabled: updatedIsScoreToolEnabled,
          },
        });
      } catch {
        setIsScoreToolEnabled(currentIsScoreToolEnabled);
      } finally {
        setIsScoreToolLoading(false);
      }
    },
    [toolId, updateAssessmentTool]
  );

  const onAddCriterion = useCallback(
    async id => {
      flushAllDebouncedFunctions();

      if (type === STANDARD_BASED_RUBRIC) {
        setStandardPutAfterItemId(id);
        return;
      }

      // Find the source criterion to copy descriptors from
      let sourceDescriptors;

      if (id) {
        // If id is provided, find the criterion with that id and use its descriptors
        const sourceCriterion = _.find(localRubricCriteria, { id });
        sourceDescriptors = sourceCriterion?.descriptors || [];
      } else {
        // If no id is provided, use the last criterion's descriptors (current behavior)
        sourceDescriptors = _.last(localRubricCriteria)?.descriptors || [];
      }

      const input = {
        toolId,
        ...DEFAULT_CRITERION_INPUT,
        descriptors: _.map(sourceDescriptors, (descriptor, index) => ({
          label: descriptor.label,
          description: "",
          displaySequence: index + 1,
          score: !_.isNil(descriptor.score) ? descriptor.score : null,
        })),
        putAfterItemId: id,
      };

      const newCriterion = await bulkCreateStandardRubricCriteria({
        input,
        type,
      });

      const idToFocus = _.first(newCriterion)?.id;

      if (idToFocus) {
        setTimeout(() => labelTextAreaRefs.current[idToFocus]?.focus?.());
      }
    },
    [
      bulkCreateStandardRubricCriteria,
      toolId,
      localRubricCriteria,
      type,
      flushAllDebouncedFunctions,
    ]
  );

  const onClickSortDescriptors = async () => {
    let nextSortDirection = null;

    if (!isStandardBasedRubric) {
      // Cycle: null -> 'asc' -> 'desc' -> null
      const sortCycle = [null, "asc", "desc"];
      const currentIndex = _.indexOf(sortCycle, sortDirection);
      nextSortDirection = sortCycle[(currentIndex + 1) % sortCycle.length];
    } else {
      // Toggle between 'asc' and 'desc', defaulting to 'asc' if null
      nextSortDirection = sortDirection === "desc" ? "asc" : "desc";
    }

    setSortDirection(nextSortDirection);

    const currentSequences = {};
    let hasChanges = false;

    const input = _.reduce(
      localRubricCriteria,
      (acc, criterion) => {
        const descriptors = _.get(criterion, "descriptors", []);

        _.forEach(descriptors, descriptor => {
          currentSequences[descriptor.id] = descriptor.displaySequence;
        });

        // Sort descriptors based on score, gradeValue, or abbreviation depending on context
        let sortedDescriptors;
        if (nextSortDirection === "asc" || nextSortDirection === "desc") {
          if (isStandardBasedRubric) {
            const validGradeValueDescriptors = [...descriptors];

            // Sort the descriptors by grade value using gradeScaleValues as reference
            sortedDescriptors = _.sortBy(
              validGradeValueDescriptors,
              descriptor => {
                const gradeScaleValueId = _.get(
                  descriptor,
                  "gradeScaleValue.id"
                );

                const matchingGradeScaleValue = _.find(gradeScaleValues, {
                  key: gradeScaleValueId,
                });

                return _.toNumber(
                  _.get(
                    matchingGradeScaleValue,
                    "gradeScaleValueScores[0].gradeValue"
                  )
                );
              }
            );

            // For descending order, reverse the sorted array
            if (nextSortDirection === "desc") {
              sortedDescriptors.reverse();
            }
          } else {
            // Check if all descriptors have null/undefined/empty scores
            const allScoresEmpty = _.every(
              descriptors,
              descriptor => _.isNil(descriptor.score) || descriptor.score === ""
            );

            if (allScoresEmpty) {
              // If all scores are empty, sort by abbreviation
              sortedDescriptors = _.sortBy(
                descriptors,
                "gradeScaleValue.abbreviation"
              );

              if (nextSortDirection === "desc") {
                sortedDescriptors.reverse();
              }
            } else {
              // Split descriptors into two groups: with valid scores and without scores
              const validScoreDescriptors = [];
              const nullScoreDescriptors = [];

              _.forEach(descriptors, descriptor => {
                // Check if score is null, undefined, or empty string
                if (_.isNil(descriptor.score) || descriptor.score === "") {
                  nullScoreDescriptors.push(descriptor);
                } else {
                  validScoreDescriptors.push(descriptor);
                }
              });

              // Sort only the descriptors with valid scores by converting to number
              const sortedValidScores = _.sortBy(
                validScoreDescriptors,
                descriptor => _.toNumber(descriptor.score)
              );

              // For descending order, reverse the valid scores array
              if (nextSortDirection === "desc") {
                sortedValidScores.reverse();
              }

              // Combine the sorted valid scores with null scores at the end
              sortedDescriptors = [
                ...sortedValidScores,
                ...nullScoreDescriptors,
              ];
            }
          }
        } else {
          // If null, maintain original order
          sortedDescriptors = descriptors;
        }

        _.forEach(sortedDescriptors, (descriptor, index) => {
          const newSequence = index + 1;

          if (currentSequences[descriptor.id] !== newSequence) {
            hasChanges = true;
          }

          acc.push({
            id: descriptor.id,
            displaySequence: newSequence,
            elementType: getElementType(criterion),
          });
        });

        return acc;
      },
      []
    );

    if (hasChanges && _.size(input) > 0) {
      setIsSortIconDisabled(true);
      await updateStandardRubricDescriptors({ toolId, input, type });
      setIsSortIconDisabled(false);
    }
  };

  const onClickDeleteCriterion = async id => {
    // delete standard in the learning goals section too, when user deletes standard rubric row
    if (isStandardBasedRubric && !isTemplate) {
      const criterion = _.find(localRubricCriteria, { id });
      const criterionStandardId = _.get(criterion, "standard.id");

      const { id: standardFieldId, uid, value } = standardField || {};

      if (_.includes(value, criterionStandardId)) {
        updateAssessmentForStandardBasedRubric({
          input: {
            assessmentId,
            fields: [
              {
                id: standardFieldId,
                value: _.filter(value, id => id !== criterionStandardId),
                valuesToAdd: [],
                valuesToRemove: [criterionStandardId],
                hasAddRemoveValues: true,
              },
            ],
          },
          uids: [uid],
        });
      }
    }

    await removeStandardRubricCriterias({ toolId, ids: [id], type });
  };

  const onClickDeleteDescriptor = async id => {
    if (!isValid) return;

    await removeStandardRubricDescriptors({ toolId, ids: [id], type });
  };

  const getNewDescriptorInputData = ({ criterion }) => {
    const standardId = _.get(criterion, "standard.id");

    if (allowGradeScaleValueSelection && standardId) {
      let sortedGradeScaleValues = [...gradeScaleValues];

      if (sortDirection) {
        sortedGradeScaleValues = _.sortBy(sortedGradeScaleValues, value => {
          const gradeValue = _.get(
            value,
            "gradeScaleValueScores[0].gradeValue"
          );
          return _.toNumber(gradeValue);
        });

        if (sortDirection === "desc") {
          sortedGradeScaleValues.reverse();
        }
      }

      const allGradeScaleValues = _.map(sortedGradeScaleValues, "key");
      const usedGradeScaleValues = _.map(
        criterion?.descriptors,
        "gradeScaleValue.id"
      );

      const availableGradeScaleValues = _.difference(
        allGradeScaleValues,
        usedGradeScaleValues
      );

      if (!_.isEmpty(availableGradeScaleValues)) {
        const gradeScaleValueId = _.first(availableGradeScaleValues);
        const gradeScaleValueObj = _.find(gradeScaleValues, {
          key: gradeScaleValueId,
        });

        const _standardGradeScaleValueDescriptor =
          _.find(standardGradeScaleValueDescriptorsMapping, {
            standardId,
            gradeScaleValueId,
          }) || {};

        const input = {
          gradeScaleValueId,
          elementId: standardId,
        };

        if (isNumericGradeScale) {
          const score = _.find(gradeScaleValues, {
            key: input.gradeScaleValueId,
          })?.score;

          if (!_.isNaN(score) && !_.isNil(score)) {
            input.score = parseFloat(score);
          }
        }

        const gradeScaleValue = {
          id: gradeScaleValueObj.key || null,
          label: gradeScaleValueObj.label || null,
          abbreviation: gradeScaleValueObj?.abbreviation || null,
          __typename: "AcademicCriteriaValue",
        };

        const standardGradeScaleValueDescriptor = {
          id: _.get(_standardGradeScaleValueDescriptor, "id", null),
          description: _.get(
            _standardGradeScaleValueDescriptor,
            "description",
            null
          ),
          gradeScaleValue: {
            id: gradeScaleValueId,
            __typename: "GradeScaleValue",
          },
          __typename: "StandardGradeScaleValueDescriptor",
        };

        return { input, gradeScaleValue, standardGradeScaleValueDescriptor };
      }

      return {};
    }

    return {};
  };

  const onAddDescriptor = async descriptorId => {
    if (!isValid) return;

    flushAllDebouncedFunctions();

    const criterion = _.find(localRubricCriteria, criterion => {
      return _.some(criterion.descriptors, { id: descriptorId });
    });

    const {
      input: descriptorInputData,
      gradeScaleValue,
      standardGradeScaleValueDescriptor,
    } = getNewDescriptorInputData({
      criterion,
    });

    const input = {
      criteriaId: criterion?.id,
      label: "",
      description: isStandardBasedRubric ? null : "",
      putAfterItemId: descriptorId,
      elementType: getElementType(criterion),
      ...descriptorInputData,
    };

    const newDescriptor = await createStandardRubricDescriptor({
      toolId,
      input,
      type,
      gradeScaleValue,
      standardGradeScaleValueDescriptor,
    });

    const idToFocus = newDescriptor?.id;

    if (idToFocus) {
      setTimeout(() => labelTextAreaRefs.current[idToFocus]?.focus?.());
    }
  };

  const onAddFirstDescriptor = async criteriaId => {
    if (!isValid) return;

    flushAllDebouncedFunctions();

    const criterion = _.find(localRubricCriteria, { id: criteriaId });
    const {
      input: descriptorInputData,
      gradeScaleValue,
      standardGradeScaleValueDescriptor,
    } = getNewDescriptorInputData({
      criterion,
    });

    const input = {
      criteriaId,
      label: "",
      description: isStandardBasedRubric ? null : "",
      putAfterItemId: null,
      elementType: getElementType(criterion),
      ...descriptorInputData,
    };

    await createStandardRubricDescriptor({
      toolId,
      input,
      type,
      gradeScaleValue,
      standardGradeScaleValueDescriptor,
    });
  };

  const updateScoreDebounced = useCallback(
    _.debounce(value => {
      updateScoreToolData({
        input: {
          id: _.get(assessmentTool, "associateScoreTool.scoreToolData.id"),
          maxScore: value,
          isCustomMaxScore: true,
        },
        toolId: assessmentTool?.id,
        type,
      });
    }, 1000),
    [assessmentTool, type]
  );

  const updateMaxScore = useCallback(value => {
    // Allow empty string for clearing the input
    if (value === "") {
      setMaxScore(value);
      calculateInputWidth(INITIAL_INPUT_WIDTH);
      return;
    }

    const isValidNumber = /^(\d+|\d+\.\d*)$/.test(value);

    if (isValidNumber && parseFloat(value) > 0) {
      setMaxScore(value);
      calculateInputWidth(value);
      updateScoreDebounced(value);
    }
  }, []);

  const onClickResetMaxScore = () => {
    setMaxScore(rubricTotalScore);

    updateScoreToolData({
      input: {
        id: _.get(assessmentTool, "associateScoreTool.scoreToolData.id"),
        maxScore: rubricTotalScore,
        isCustomMaxScore: true,
      },
      toolId: assessmentTool?.id,
      type,
    });
  };

  const onClickAddRow = useCallback(() => {
    if (isStandardBasedRubric) {
      setStandardPutAfterItemId(_.last(localRubricCriteria)?.id);
    } else {
      onAddCriterion();
    }
  }, [localRubricCriteria, isStandardBasedRubric]);

  const onSelectStandards = async params => {
    const {
      selectedStandards: selectedStandardsObj,
      selectedGradeScale,
      elementType: elementTypeObj,
    } = params;

    // Flatten per-set objects to the flat shapes the rest of this handler expects.
    const selectedStandards = Object.values(selectedStandardsObj).flat();
    const elementType = _.values(elementTypeObj)[0];

    const gradeScaleId = _.get(selectedGradeScale, "id") || gradeScale?.id;

    const addedStandards = _.filter(
      selectedStandards,
      id => !_.includes(initialSelectedStandards, id)
    );

    const removedStandards = _.filter(
      initialSelectedStandards,
      id => !_.includes(selectedStandards, id)
    );

    if (!_.isEmpty(addedStandards)) {
      const response = await bulkCreateStandardRubricCriteria({
        input: _.map(addedStandards, id => ({
          elementId: id,
          gradeScaleId,
          toolId,
          elementType,
        })),
        type,
      });

      // Create a new array by inserting response after standardPutAfterItemId
      if (!_.isEmpty(response)) {
        let updatedRubricCriteria = [];

        // If we don't have a specific position to insert after, just append
        if (!standardPutAfterItemId) {
          updatedRubricCriteria = [...localRubricCriteria, ...response];
        } else {
          // Find the index of the item after which to insert
          const insertIndex = _.findIndex(localRubricCriteria, {
            id: standardPutAfterItemId,
          });

          // If item not found, append to the end
          if (insertIndex === -1) {
            updatedRubricCriteria = [...localRubricCriteria, ...response];
          } else {
            // Insert after the specified item
            updatedRubricCriteria = [...localRubricCriteria];
            updatedRubricCriteria.splice(insertIndex + 1, 0, ...response);
          }
        }

        await delay(1000);
        await updateStandardRubricCriterias({
          input: _.map(updatedRubricCriteria, ({ id }, index) => ({
            id,
            displaySequence: index + 1,
          })),
          toolId,
          type,
        });
      }
    }

    if (!_.isEmpty(removedStandards)) {
      // Find criteria IDs to remove based on standard IDs
      const criteriaIdsToRemove = _.map(
        _.filter(localRubricCriteria, criterion =>
          _.includes(removedStandards, _.get(criterion, "standard.id"))
        ),
        "id"
      );

      if (!_.isEmpty(criteriaIdsToRemove)) {
        await removeStandardRubricCriterias({
          toolId,
          type,
          ids: criteriaIdsToRemove,
        });
      }
    }

    setStandardPutAfterItemId(null);
  };

  const onChangeIsDropdownVisible = useCallback(value => {
    setIsDropdownVisible(value);
  }, []);

  const allRubricItems = useMemo(() => {
    return _.flatMap(localRubricCriteria, criterion => [
      criterion,
      ..._.get(criterion, "descriptors", []),
    ]);
  }, [localRubricCriteria]);

  const maxScoreInputStyle = useMemo(() => {
    return { ...styles.inputStyle, width: inputWidth };
  }, [inputWidth]);

  const initialSelectedStandards = useMemo(() => {
    const standardIds = _.map(localRubricCriteria, criterion => {
      return _.get(criterion, "standard.id");
    });

    return _.compact(standardIds);
  }, [localRubricCriteria]);

  const initialStandardSet = useMemo(() => {
    return _.get(
      _.first(localRubricCriteria),
      "standard.standardsWrapper.standardSet"
    );
  }, [localRubricCriteria]);

  const initialSelectedStandardsMap = useMemo(
    () =>
      initialStandardSet?.id
        ? { [initialStandardSet.id]: initialSelectedStandards }
        : {},
    [initialStandardSet, initialSelectedStandards]
  );

  const sortIcon = useMemo(() => {
    if (sortDirection === null) {
      return (
        <UpDownArrowOutlined variant="subtle" style={styles.arrowOutlined} />
      );
    }

    if (sortDirection === "asc") {
      return <RightArrowOutlined variant="subtle" />;
    }

    return <LeftArrowOutlined variant="subtle" />;
  }, [sortDirection]);

  // classes

  const headerClass = classNames(
    classes.headerRow,
    isStandardBasedRubric &&
      isNumericGradeScale &&
      classes.headerRowWithTotalPoints
  );

  const pointsClass = classNames(
    "text-label",
    classes.headerCell,
    classes.totalPointsHeaderCell
  );

  const totalPointsClass = classNames(classes.totalPointsCell, {
    [classes.totalPointsCellWithBorderLeft]: !allowGradeScaleValueSelection,
  });

  const getScoreInputElement = () => {
    return isScoreToolLoading ? (
      <SkeletonLoader width={inputWidth} height={"28px"} />
    ) : (
      <FormTextInput
        dsVersion="2.0"
        size={"small"}
        value={maxScore}
        style={maxScoreInputStyle}
        containerStyle={styles.inputContainerStyle}
        placeholder={"-"}
        updateInputField={updateMaxScore}
        testId={`${testId}-score-input`}
        customErrorClass={classes.customErrorClass}
        disabled={!isValid || true} // we'll allow overriding the max score later
      />
    );
  };

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragMove={onDragMove}
        modifiers={[restrictToParentElement]}
      >
        <DSProvider dsVersion="2.0">
          <div className={classes.container}>
            {showAlert && isMaxScoreOverridden ? (
              <div className={classes.alertContainer}>
                <Alert
                  type="warning"
                  message={t(
                    "classRoom:score_based_rubric_reset_max_score_alert_msg"
                  )}
                  isClosable={true}
                  actionElement={
                    <Button
                      variant={"neutral"}
                      type={"outlined"}
                      size={"medium"}
                      icon={<ReloadArrowOutlined />}
                      onClick={onClickResetMaxScore}
                    >
                      {t("classRoom:reset_maximum_score")}
                    </Button>
                  }
                  onClose={onCloseAlert}
                />
              </div>
            ) : null}

            {/* Header */}
            <header className={headerClass}>
              <div className={classes.flex}>
                <div className={classNames("text-label", classes.headerCell)}>
                  {t("common:criteria")}
                </div>
              </div>

              <div className={classes.headerCell}>
                <span className="text-label">
                  {t("common:capitalize", { text: t("common:ratings") })}
                </span>

                {showSortingOption ? (
                  <IconButton
                    icon={sortIcon}
                    variant="neutral"
                    type="plain"
                    size="x-small"
                    onClick={onClickSortDescriptors}
                    ref={sortButtonRef}
                    disabled={!isValid || isSortIconDisabled}
                  />
                ) : null}
              </div>

              {isStandardBasedRubric && isNumericGradeScale && (
                <div className={pointsClass}>{t("common:points")}</div>
              )}
            </header>

            {/* sortable criteria list (row) */}
            <SortableContext
              items={criteriaItems}
              strategy={verticalListSortingStrategy}
            >
              <div className={classes.rowsContainer}>
                {_.map(localRubricCriteria, criterion => {
                  const { descriptors } = criterion || {};

                  const duplicateDescriptorScores = findDuplicates(
                    _.map(descriptors, "score")
                  );

                  const duplicateGradeValues = findDuplicates(
                    _.map(descriptors, "gradeScaleValue.id")
                  );

                  const showTotalPoints =
                    isStandardBasedRubric && isNumericGradeScale;

                  const customRef = ref => {
                    rubricEditCellRefs.current[criterion.id] = ref;
                  };

                  return (
                    <SortableRow
                      key={criterion.id}
                      criterion={criterion}
                      showPopover={showCriteriaDeleteButton}
                      onClickDeleteCriterion={onClickDeleteCriterion}
                      testId={testId}
                      labelTextAreaRefs={labelTextAreaRefs}
                      items={allRubricItems}
                      sortButtonRef={sortButtonRef}
                      onAddCriterion={onAddCriterion}
                      isValid={isValid}
                      showTotalPoints={showTotalPoints}
                    >
                      <StandardRubricEditCell
                        customRef={customRef}
                        item={criterion}
                        descriptionConfig={descriptionConfig}
                        isCriteria={true}
                        isDragging={isDragging}
                        toolId={toolId}
                        onAddDescriptor={onAddFirstDescriptor}
                        saveStandardRubricUpdateActivityStatus={
                          saveStandardRubricUpdateActivityStatus
                        }
                        labelTextAreaRefs={labelTextAreaRefs}
                        descriptionTextAreaRefs={descriptionTextAreaRefs}
                        testId={testId}
                        items={allRubricItems}
                        localRubricCriteria={localRubricCriteria}
                        isStandardBasedRubric={isStandardBasedRubric}
                        showScore={showScore}
                        type={type}
                        allowGradeScaleValueSelection={
                          allowGradeScaleValueSelection
                        }
                        gradeScaleValues={gradeScaleValues}
                      />

                      {/* sortable descriptors list (cells in a row except for the first one) */}
                      <SortableContext
                        items={_.map(descriptors, "id")}
                        strategy={horizontalListSortingStrategy}
                      >
                        <div className={classes.descriptorsContainer}>
                          {_.map(descriptors, (descriptor, index) => {
                            const showAddIcon =
                              !isStandardBasedRubric ||
                              (allowGradeScaleValueSelection &&
                                _.size(descriptors) < _.size(gradeScaleValues));

                            const isLastDescriptor =
                              index === _.size(descriptors) - 1;

                            const customRef = ref => {
                              rubricEditCellRefs.current[descriptor.id] = ref;
                            };

                            return (
                              <SortableDescriptor
                                key={descriptor.id}
                                descriptor={descriptor}
                                rowId={criterion.id}
                                showPopover={
                                  _.size(descriptors) > 1 && !isDropdownVisible
                                }
                                onClickDeleteDescriptor={
                                  onClickDeleteDescriptor
                                }
                                testId={testId}
                                labelTextAreaRefs={labelTextAreaRefs}
                                items={allRubricItems}
                                onAddDescriptor={onAddDescriptor}
                                isValid={isValid}
                                isStandardBasedRubric={isStandardBasedRubric}
                                allowGradeScaleValueSelection={
                                  allowGradeScaleValueSelection
                                }
                                showAddIcon={showAddIcon}
                              >
                                <StandardRubricEditCell
                                  customRef={customRef}
                                  item={descriptor}
                                  descriptionConfig={descriptionConfig}
                                  duplicateDescriptorScores={
                                    duplicateDescriptorScores
                                  }
                                  duplicateGradeValues={duplicateGradeValues}
                                  isDragging={isDragging}
                                  pointTextAreaRefs={pointTextAreaRefs}
                                  labelTextAreaRefs={labelTextAreaRefs}
                                  descriptionTextAreaRefs={
                                    descriptionTextAreaRefs
                                  }
                                  toolId={toolId}
                                  onAddDescriptor={onAddDescriptor}
                                  saveStandardRubricUpdateActivityStatus={
                                    saveStandardRubricUpdateActivityStatus
                                  }
                                  testId={testId}
                                  items={allRubricItems}
                                  sortDirection={sortDirection}
                                  localRubricCriteria={localRubricCriteria}
                                  isStandardBasedRubric={isStandardBasedRubric}
                                  showScore={showScore}
                                  type={type}
                                  allowGradeScaleValueSelection={
                                    allowGradeScaleValueSelection
                                  }
                                  isNumericGradeScale={isNumericGradeScale}
                                  gradeScaleValues={gradeScaleValues}
                                  onChangeIsDropdownVisible={
                                    onChangeIsDropdownVisible
                                  }
                                  standardGradeScaleValueDescriptorsMapping={
                                    standardGradeScaleValueDescriptorsMapping
                                  }
                                  isLastDescriptor={isLastDescriptor}
                                />
                              </SortableDescriptor>
                            );
                          })}
                        </div>
                      </SortableContext>

                      {/* Total Points Column */}
                      {isStandardBasedRubric && isNumericGradeScale && (
                        <div className={totalPointsClass}>
                          <span className="text-body">
                            {criterion?.maxScore}
                          </span>
                        </div>
                      )}
                    </SortableRow>
                  );
                })}
              </div>
            </SortableContext>

            {/* bottom controls section */}
            <div className={classes.bottomContainer}>
              <div className={classes.buttons}>
                <Button
                  size="small"
                  variant="neutral"
                  type="outlined"
                  icon={<AddCircleOutlined />}
                  onClick={onClickAddRow}
                  disabled={!isValid}
                  testId={`${testId}-addCriterion-button`}
                >
                  {t("common:add_with_label", {
                    label: t("common:lowercase", { text: t("common:row") }),
                  })}
                </Button>

                {showScoreForNumericStandardBasedRubric || showScore ? (
                  <>
                    <span className={classes.divider} />

                    <div className={classes.rubricTotalContainer}>
                      <span className="text-body">
                        {t("classRoom:maximum_score")}:
                      </span>

                      {showScoreForNumericStandardBasedRubric ? (
                        <ToggleSwitch
                          checked={showScore}
                          onClick={() => onToggleMaxScore(showScore)}
                          testId={`${testId}-max-score-toggle`}
                          dsVersion="2.0"
                        />
                      ) : null}

                      {showScore ? getScoreInputElement() : null}
                    </div>
                  </>
                ) : null}
              </div>

              <div className={classes.buttons}>
                <ChecklistDropdown
                  options={displayOptions}
                  value={_.map(descriptionConfig, "value")}
                  onChange={onChangeDisplayOption}
                  testId={`${testId}-displayOptions-dropdown`}
                  showSelectedOptionsOnTop={false}
                  placement={"topRight"}
                >
                  <Button
                    size="small"
                    variant="neutral"
                    type="outlined"
                    icon={<SortOutlined />}
                    testId={`${testId}-displayOptions-button`}
                  >
                    {t("common:display")}
                  </Button>
                </ChecklistDropdown>

                {showSaveTemplateButton ? (
                  <Button
                    size="small"
                    variant="neutral"
                    type="outlined"
                    icon={<AssessmentAddOutlined />}
                    onClick={onClickToolSave}
                    testId={`${testId}-saveTemplate-button`}
                  >
                    {t("classRoom:save_as_template")}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </DSProvider>
      </DndContext>

      {standardPutAfterItemId ? (
        <StandardsSelectionModal
          mode={"edit"}
          onClose={() => setStandardPutAfterItemId(null)}
          onSelectClick={onSelectStandards}
          learningCourseId={learningCourseId}
          initialSelectedStandards={initialSelectedStandardsMap}
          initialStandardSet={initialStandardSet}
          assessmentId={assessmentId}
          shouldSyncStandards={!isTemplate}
          canMapGradeScale={true}
        />
      ) : null}
    </>
  );
});

StandardRubricEdit.displayName = "StandardRubricEdit";

const mapActionCreators = {
  updateSettings,
  updateAssessmentForStandardBasedRubric,
  updateScoreToolData,
  updateAssessmentTool,
  reorderAssessmentToolItem,
  updateStandardRubricCriterias,
  bulkCreateStandardRubricCriteria,
  removeStandardRubricCriterias,
  removeStandardRubricDescriptors,
  createStandardRubricDescriptor,
  updateStandardRubricDescriptors,
  saveAssessmentToolUpdateStatusInRedux,
};

const mapStateToProps = (state, ownProps) => {
  const { assessmentId } = ownProps;

  const curriculumProgramId = getSelectedCurriculumId(state);
  const curriculumProgramType = getCurrentCurriculumProgramType(state);
  const academicYearId = getSelectedAcademicYearId(state);

  const assessmentDetails = getAssessmentDetailsFromCache({
    id: assessmentId,
    collaboratorsFilter: {
      userAcademicYearId: academicYearId,
    },
  });

  const standardUID = STANDARD_UIDS[curriculumProgramType];
  const standardField = _.find(assessmentDetails?.allFields, {
    uid: standardUID,
  });

  return { curriculumProgramId, curriculumProgramType, standardField };
};

const getDerivedProps = ({ assessmentTool }) => {
  const updatedAssessmentTool = getTransformedAssessmentTool(assessmentTool);

  const standardIds = _.compact(
    _.map(updatedAssessmentTool?.standardRubricCriterias, "standard.id")
  );

  return {
    standardIds,
    assessmentTool: updatedAssessmentTool,
  };
};

export default compose(
  withDerivedProps(getDerivedProps),
  connect(mapStateToProps, mapActionCreators),
  graphql(getGradeScaleValuesQuery, {
    name: "getGradeScaleValues",
    skip: ({ assessmentTool }) => _.isEmpty(assessmentTool?.gradeScale?.id),
    options: ({ assessmentTool, curriculumProgramId }) => {
      const gradeScaleId = _.get(assessmentTool, "gradeScale.id");

      const variables = {
        id: gradeScaleId,
        filters: {
          entityType: "CURRICULUM_PROGRAM",
          entityDetails: {
            curriculumProgramId,
            gradeScaleId: gradeScaleId,
          },
        },
      };

      return {
        fetchPolicy: "cache-and-network",
        variables,
      };
    },
    props({
      getGradeScaleValues: { node, networkStatus },
      ownProps: {
        assessmentTool,
        isData,
        isLoading,
        type = SCORE_BASED_RUBRIC,
      },
    }) {
      const isNumericGradeScale =
        _.get(assessmentTool, "gradeScale.gradingType") === "NUMERIC";
      const sortedGradeScaleValues = _.sortBy(
        _.get(node, "gradeScaleEntityDetails.0.gradeScaleValues", []),
        "displaySequence"
      );

      const gradeScaleValues = _.chain(sortedGradeScaleValues)
        .filter(value => !_.isEmpty(value?.abbreviation))
        .map(value => ({
          key: value?.id,
          label: value?.label,
          score: isNumericGradeScale ? value?.abbreviation : null,
          abbreviation: value?.abbreviation,
          gradeScaleValueScores: value?.gradeScaleValueScores || [],
        }))
        .value();

      const allowGradeScaleValueSelection = getIsGradeScaleValueSelectionAllowed(
        type
      );

      return {
        // if gradeScaleValues is empty it means there is no option to select in the dropdown
        // so eventually allowGradeScaleValueSelection will not be allowed so we will render normal UI without dropdown
        allowGradeScaleValueSelection:
          allowGradeScaleValueSelection && !_.isEmpty(gradeScaleValues),
        gradeScaleValues,
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),

  // for curriculum other than PYP fetch data from planner_element_node
  graphql(getStandardGradeScaleDescriptorsQuery, {
    name: "getStandardGradeScaleDescriptors",
    skip: ({
      standardIds,
      allowGradeScaleValueSelection,
      curriculumProgramType,
    }) =>
      !allowGradeScaleValueSelection ||
      _.isEmpty(standardIds) ||
      curriculumProgramType === CURRICULUM_TYPE_PYP,
    options: ({ standardIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ids: standardIds,
      },
    }),
    props({
      getStandardGradeScaleDescriptors: { multiNode, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const standardGradeScaleValueDescriptorsMapping = _.reduce(
        multiNode,
        (acc, node) => {
          const standardGradeScaleValueDescriptor = _.get(
            node,
            "standardsWrapper.standardGradeScaleValueDescriptor",
            []
          );

          _.forEach(standardGradeScaleValueDescriptor, descriptor => {
            const { id, gradeScaleValue, description } = descriptor || {};
            const gradeScaleValueId = _.get(gradeScaleValue, "id", null);

            acc.push({
              id,
              standardId: node.id,
              gradeScaleValueId,
              description,
            });
          });

          return acc;
        },
        []
      );

      return {
        standardGradeScaleValueDescriptorsMapping,
        isData: !_.isEmpty(multiNode) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),

  // For PYP fetch data from benchmark
  graphql(getBenchmarkGradeScaleDescriptorsQuery, {
    name: "getBenchmarkGradeScaleDescriptors",
    skip: ({
      standardIds,
      allowGradeScaleValueSelection,
      curriculumProgramType,
    }) =>
      !allowGradeScaleValueSelection ||
      _.isEmpty(standardIds) ||
      curriculumProgramType !== CURRICULUM_TYPE_PYP,
    options: ({ standardIds }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ids: standardIds,
      },
    }),
    props({
      getBenchmarkGradeScaleDescriptors: { multiNode, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const standardGradeScaleValueDescriptorsMapping = _.reduce(
        multiNode,
        (acc, node) => {
          const standardGradeScaleValueDescriptor = _.get(
            node,
            "standardsWrapper.standardGradeScaleValueDescriptor",
            []
          );

          _.forEach(standardGradeScaleValueDescriptor, descriptor => {
            const { id, gradeScaleValue, description } = descriptor || {};
            const gradeScaleValueId = _.get(gradeScaleValue, "id", null);

            acc.push({
              id,
              standardId: node.id,
              gradeScaleValueId,
              description,
            });
          });

          return acc;
        },
        []
      );

      return {
        standardGradeScaleValueDescriptorsMapping,
        isData: !_.isEmpty(multiNode) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(StandardRubricEdit);
